
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { format, isBefore, isToday, addDays, differenceInDays } from "date-fns";
import { 
  MoreHorizontal, 
  Trash, 
  Edit, 
  Calendar, 
  Package, 
  MapPin, 
  Barcode,
  AlertTriangle,
  CheckCircle
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { InventoryItem } from "@/types/inventory";
import { useNavigate } from "react-router-dom";

interface InventoryListProps {
  items: InventoryItem[];
  loading: boolean;
  onRefresh: () => void;
}

const InventoryList = ({ items, loading, onRefresh }: InventoryListProps) => {
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const navigate = useNavigate();
  
  const getExpiryStatusColor = (expiryDate: string | null, hasExpiry: boolean) => {
    if (!hasExpiry || !expiryDate) return "bg-gray-100 text-gray-800";
    
    const today = new Date();
    const expiry = new Date(expiryDate);
    
    if (isBefore(expiry, today)) {
      return "bg-red-100 text-red-800";
    }
    
    const daysUntilExpiry = differenceInDays(expiry, today);
    
    if (daysUntilExpiry <= 3) {
      return "bg-red-100 text-red-800";
    } else if (daysUntilExpiry <= 7) {
      return "bg-amber-100 text-amber-800";
    } else {
      return "bg-green-100 text-green-800";
    }
  };
  
  const getExpiryStatus = (expiryDate: string | null, hasExpiry: boolean) => {
    if (!hasExpiry) return "No Expiration";
    if (!expiryDate) return "Unknown";
    
    const today = new Date();
    const expiry = new Date(expiryDate);
    
    if (isBefore(expiry, today)) {
      return "Expired";
    }
    
    if (isToday(expiry)) {
      return "Expires Today";
    }
    
    const daysUntilExpiry = differenceInDays(expiry, today);
    
    if (daysUntilExpiry === 1) {
      return "Expires Tomorrow";
    } else if (daysUntilExpiry <= 7) {
      return `Expires in ${daysUntilExpiry} days`;
    } else {
      return `Expires ${format(expiry, "MMM d, yyyy")}`;
    }
  };
  
  const handleDeleteItem = async (id: string) => {
    try {
      setDeletingItemId(id);
      
      const { data: userData } = await supabase.auth.getUser();
      
      if (!userData?.user?.id) {
        throw new Error("User not authenticated");
      }

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('persona')
        .eq('id', userData.user.id)
        .maybeSingle();
      
      if (profileError) throw profileError;
      
      const persona = profileData?.persona || 'household';
      
      // First delete from persona-specific table using literal table names
      if (persona === 'household') {
        const { error: deleteError } = await supabase
          .from('household_inventory')
          .delete()
          .eq('id', id);
          
        if (deleteError) throw deleteError;
      } else if (persona === 'restaurant') {
        const { error: deleteError } = await supabase
          .from('restaurant_inventory')
          .delete()
          .eq('id', id);
          
        if (deleteError) throw deleteError;
      } else if (persona === 'retail') {
        const { error: deleteError } = await supabase
          .from('retail_inventory')
          .delete()
          .eq('id', id);
          
        if (deleteError) throw deleteError;
      } else if (persona === 'healthcare') {
        const { error: deleteError } = await supabase
          .from('healthcare_inventory')
          .delete()
          .eq('id', id);
          
        if (deleteError) throw deleteError;
      }
        
      // Then delete from main inventory_items table
      const { error: mainDeleteError } = await supabase
        .from('inventory_items')
        .delete()
        .eq('id', id);
        
      if (mainDeleteError) throw mainDeleteError;
      
      toast.success("Item deleted successfully", {
        position: "bottom-right",
        duration: 3000,
      });
      
      onRefresh();
    } catch (error: any) {
      console.error("Error deleting item:", error.message);
      toast.error("Failed to delete item");
    } finally {
      setDeletingItemId(null);
    }
  };
  
  const handleEditItem = (itemId: string) => {
    navigate(`/inventory/edit/${itemId}`);
  };
  
  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((index) => (
          <Card key={index}>
            <CardContent className="p-6">
              <div className="flex justify-between">
                <div className="space-y-2">
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-4 w-60" />
                </div>
                <Skeleton className="h-8 w-24" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }
  
  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p className="text-muted-foreground">No items match your current filters.</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-4">
      {items.map((item) => (
        <Card key={item.id} className="overflow-hidden">
          <CardContent className="p-0">
            <div className="flex flex-col md:flex-row">
              {item.image_url && (
                <div className="w-full md:w-24 h-24 bg-gray-100 flex-shrink-0">
                  <img 
                    src={item.image_url} 
                    alt={item.name} 
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              <div className="flex-grow p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-lg">{item.name}</h3>
                    {item.description && (
                      <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                    )}
                  </div>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEditItem(item.id)}>
                        <Edit className="mr-2 h-4 w-4" /> Edit Item
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => handleDeleteItem(item.id)}
                        disabled={deletingItemId === item.id}
                      >
                        <Trash className="mr-2 h-4 w-4" /> 
                        {deletingItemId === item.id ? "Deleting..." : "Delete Item"}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                
                <div className="flex flex-wrap gap-2 mt-3">
                  <Badge variant="secondary">
                    <Package className="mr-1 h-3 w-3" /> {item.category}
                  </Badge>
                  
                  <Badge variant="secondary">
                    Qty: {item.quantity}
                  </Badge>
                  
                  <Badge className={getExpiryStatusColor(item.expiry_date, item.has_expiry)}>
                    <Calendar className="mr-1 h-3 w-3" /> 
                    {getExpiryStatus(item.expiry_date, item.has_expiry)}
                  </Badge>
                  
                  {item.location && (
                    <Badge variant="outline">
                      <MapPin className="mr-1 h-3 w-3" /> {item.location}
                    </Badge>
                  )}
                  
                  {item.barcode && (
                    <Badge variant="outline">
                      <Barcode className="mr-1 h-3 w-3" /> {item.barcode}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default InventoryList;
